import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVideoPosterUrl } from '@/utils/mediaUtils';
import { ChevronLeft, ChevronRight, X } from 'react-feather';
import { SliderDots } from '@/components/slider-dots/SliderDots';
import styles from './DraftFeedbackModal.module.css';

import CheckIcon from '../../../../../../public/assets/icons/check.svg';
import MoreIcon from '../../../../../../public/assets/icons/more.svg';
import MoreBlueIcon from '../../../../../../public/assets/icons/not_review.svg';

export interface FeedbackType {
  type: string;
  media_id: string;
  status: 'REJECTED' | 'APPROVED' | 'NOT_REVIEWED';
  caption?: string | null;
  title?: string;
  feedback_cm?: string | null;
  feedback_client?: string | null;
}

interface PreviewModalProps {
  initialItem: string;
  items: (DraftMedia | FeedbackType)[];
  onClose: () => void;
}

export function DraftFeedbackModal({ initialItem, items, onClose }: PreviewModalProps) {
  const [startX, setStartX] = useState<number | null>(null);
  const { t } = useTranslation();

  const [currentIndex, setCurrentIndex] = useState(
    items.findIndex((item) => item.media_id === initialItem)
  );

  useEffect(() => {
    // document.body.style.overflow = 'hidden';
    // // save current position
    const scrollPosition = window.pageYOffset;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    return () => {
      // document.body.style.overflow = 'auto';
      document.body.style.position = '';
      document.body.style.top = '';

      window.scrollTo(0, scrollPosition);
    };
  }, []);

  const currentItem = items[currentIndex];

  // Guard against undefined currentItem
  if (!currentItem) {
    return null;
  }

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === items.length - 1 ? 0 : prevIndex + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? items.length - 1 : prevIndex - 1));
  };

  function getMediaIcon(status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED', active?: boolean) {
    if (status === 'APPROVED') {
      return <CheckIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'REJECTED') {
      return <MoreIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'NOT_REVIEWED') {
      return <MoreBlueIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    return undefined;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startX === null) return;

    const endX = e.changedTouches[0].clientX;
    const diffX = startX - endX;

    if (diffX > 50) {
      // left swipe
      handleNext();
    } else if (diffX < -50) {
      // right swipe
      handlePrev();
    }

    setStartX(null); // reset startX
  };

  const hideFeedback =
    'resource_type' in currentItem && !currentItem.feedback_cm && !currentItem.feedback_client;

  return (
    <div className={styles.modal}>
      <button className={styles.modalClose} onClick={onClose} type="button">
        <X size={30} />
      </button>

      <div
        className={styles.modalInfo}
        onClick={(e: TypeAny) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {'resource_type' in currentItem && currentItem.url && (
          <>
            {currentItem.resource_type === 'image' ? (
              <img src={currentItem.url} alt="" />
            ) : (
              <video
                controls
                playsInline
                src={currentItem.url}
                poster={getVideoPosterUrl(currentItem.url)}
              />
            )}
            <div className={styles.modalButton}>
              <SliderDots currentIndex={currentIndex} itemsLength={items.length} />
            </div>
          </>
        )}

        {!hideFeedback && (
          <div className={styles.feedback}>
            {currentItem.media_id === 'feedback' && 'caption' in currentItem && (
              <div className={styles.feedbackCaption}>
                <div className={styles.captionTitle}>{currentItem.title}</div>
                <div className={styles.captionText}>{currentItem.caption}</div>
              </div>
            )}
            <div className={styles.feedbackItem}>
              <div className={styles.feedbackTitle}>
                {getMediaIcon(currentItem.status)}

                {currentItem.status !== 'NOT_REVIEWED' && (
                  <span className={`${styles.feedbackStatus} ${styles[currentItem.status]}`}>
                    {currentItem.status}
                  </span>
                )}
                {currentItem.media_id === 'general' && <span>General</span>}
              </div>
              <div className={styles.feedbackBody}>
                {currentItem.feedback_cm && (
                  <>
                    <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                    {currentItem.feedback_cm}
                  </>
                )}
                {currentItem.feedback_client && (
                  <>
                    <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                    {currentItem.feedback_client}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {!('resource_type' in currentItem) && (
          <div className={styles.modalButton}>
            <SliderDots currentIndex={currentIndex} itemsLength={items.length} />
          </div>
        )}
      </div>

      {items.length > 1 && (
        <>
          <span className={styles.prevBtn} onClick={handlePrev}>
            <ChevronLeft />
          </span>
          <span className={styles.nextBtn} onClick={handleNext}>
            <ChevronRight />
          </span>
        </>
      )}
    </div>
  );
}
